import { ORDER_STATUS } from '@bagonboard/wiseman';
import { assoc, prop } from 'ramda';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { actions } from '#store';
import services from '#services';
import { useSnackbar } from '#hooks';

const useOrder = () => {
  const { enqueueErrorSnackbar } = useSnackbar();
  const state = useSelector(prop('order'));
  const dispatch = useDispatch();

  const fetch = useCallback(async (id, { query, enableSnackbar } = {}) => {
    try {
      dispatch(actions.order.fetch.creator());
      const order = await services.bob.order.fetch(id, query);
      dispatch(actions.order.fetchSuccess.creator({ payload: order }));

      return order;
    } catch (err) {
      dispatch(actions.order.fetchError.creator({ error: err.message }));
      if (enableSnackbar) {
        enqueueErrorSnackbar(err.message, {
          persist: true,
          buttons: [{
            text   : 'retry',
            onClick: () => fetch(id, { query, enableSnackbar }),
          }],
        });
        return null;
      }
      throw err;
    }
  }, [dispatch]);

  const update = useCallback(async (id, { values, enableSnackbar } = {}) => {
    try {
      dispatch(actions.order.update.creator());
      const order = await services.bob.order.update({ order: assoc('_id', id, values) });
      dispatch(actions.order.updateSuccess.creator({ payload: order }));
      return order;
    } catch (err) {
      dispatch(actions.order.updateError.creator({ error: err.message }));
      if (enableSnackbar) {
        enqueueErrorSnackbar(err.message, {
          persist: true,
          buttons: [{
            text   : 'retry',
            onClick: () => update(id, { values, enableSnackbar }),
          }],
        });
        return null;
      }
      throw err;
    }
  }, [dispatch]);

  const confirm = useCallback(async (id, { enableSnackbar } = {}) => {
    try {
      dispatch(actions.order.confirm.creator());
      const order = await services.bob.order.update({
        order: {
          _id   : id,
          status: ORDER_STATUS.CONFIRMED,
        },
      });
      dispatch(actions.order.confirmSuccess.creator({ payload: order }));
      return order;
    } catch (err) {
      dispatch(actions.order.confirmError.creator({ error: err.message }));
      if (enableSnackbar) {
        enqueueErrorSnackbar(err.message, {
          persist: true,
          buttons: [{
            text   : 'retry',
            onClick: () => confirm(id, { enableSnackbar }),
          }],
        });
        return null;
      }
      throw err;
    }
  }, [dispatch]);

  const events = useCallback(async (id, event, { enableSnackbar } = {}) => {
    try {
      dispatch(actions.order.events.creator());
      const order = await services.bob.order.events({
        event,
        payload: {
          order: {
            _id: id,
          },
        },
      });
      dispatch(actions.order.eventsSuccess.creator({ payload: order }));
      return order;
    } catch (err) {
      dispatch(actions.order.eventsError.creator({ error: err.message }));
      if (enableSnackbar) {
        enqueueErrorSnackbar(err.message, {
          persist: true,
          buttons: [{
            text   : 'retry',
            onClick: () => events(id, event, { enableSnackbar }),
          }],
        });
        return null;
      }
      throw err;
    }
  }, [dispatch]);

  const resetEvents = useCallback(() => dispatch(actions.order.resetEvents.creator()), [dispatch]);

  return {
    ...state,
    handlers: {
      fetch,
      update,
      confirm,
      events,
      resetEvents,
    },
  };
};

export default useOrder;
